import React, { useState } from 'react';
import {
    Form,
    Input,
    Button,
    Typography,
    message,
    Space,
    Modal
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import * as adminactions from "store/actions/AuthActions";

const { Title, Text } = Typography;

const SetPassword = ({ visible, onCancel, onSuccess, user }) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    const handleSetPassword = async (values) => {
        if (!user?.id) {
            message.error('No user associated with this employee');
            return;
        }
        setLoading(true);
        try {
            const response = await adminactions.setUserPassword(
                values.password,
                values.confirmPassword,
                user.id
            );

            if (response && response.message && !response.error) {
                message.success('Password set successfully!');
                form.resetFields();
                onSuccess && onSuccess();
            } else {
                message.error(response?.error || response?.message || 'Failed to set password');
            }
        } catch (error) {
            message.error('Failed to set password');
        } finally {
            setLoading(false);
        }
    };

    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('Please enter password!'));
        }
        if (value.length < 8) {
            return Promise.reject(new Error('Password must be at least 8 characters!'));
        }
        if (!/(?=.*[a-z])/.test(value)) {
            return Promise.reject(new Error('Password must contain at least one lowercase letter!'));
        }
        if (!/(?=.*[A-Z])/.test(value)) {
            return Promise.reject(new Error('Password must contain at least one uppercase letter!'));
        }
        if (!/(?=.*\d)/.test(value)) {
            return Promise.reject(new Error('Password must contain at least one number!'));
        }
        return Promise.resolve();
    };

    return (
        <Modal
            title={
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px auto'
                    }}>
                        <LockOutlined style={{ fontSize: 20, color: 'white' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#1a1a1a' }}>
                        Set Password
                    </Title>
                    {user && (
                        <Text style={{ color: '#666', fontSize: 14 }}>
                            Set password for {user.first_name} {user.last_name}
                        </Text>
                    )}
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={480}
            centered
            destroyOnClose
        >
            <Form
                form={form}
                onFinish={handleSetPassword}
                layout="vertical"
                size="large"
                style={{ marginTop: 16 }}
            >
                <Form.Item
                    name="password"
                    label="New Password"
                    rules={[{ validator: validatePassword }]}
                    hasFeedback
                >
                    <Input.Password
                        placeholder="Enter new password"
                        style={{
                            height: 48,
                            borderRadius: 8,
                            fontSize: 16
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="confirmPassword"
                    label="Confirm Password"
                    dependencies={['password']}
                    hasFeedback
                    rules={[
                        { required: true, message: 'Please confirm your password!' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Passwords do not match!'));
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        placeholder="Confirm new password"
                        style={{
                            height: 48,
                            borderRadius: 8,
                            fontSize: 16
                        }}
                    />
                </Form.Item>

                <div style={{ 
                    background: '#f6f8fa', 
                    padding: 16, 
                    borderRadius: 8, 
                    marginBottom: 24,
                    border: '1px solid #e1e4e8'
                }}>
                    <Text strong style={{ color: '#24292e', fontSize: 14, display: 'block', marginBottom: 8 }}>
                        Password Requirements:
                    </Text>
                    <ul style={{ margin: 0, paddingLeft: 16, color: '#586069', fontSize: 13 }}>
                        <li>At least 8 characters long</li>
                        <li>At least one lowercase letter</li>
                        <li>At least one uppercase letter</li>
                        <li>At least one number</li>
                    </ul>
                </div>

                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loading}
                        style={{
                            height: 48,
                            borderRadius: 8,
                            fontSize: 16,
                            fontWeight: 500
                        }}
                    >
                        Set Password
                    </Button>

                    <Button
                        block
                        onClick={onCancel}
                        style={{
                            height: 48,
                            borderRadius: 8,
                            fontSize: 16
                        }}
                    >
                        Cancel
                    </Button>
                </Space>
            </Form>
        </Modal>
    );
};

export default SetPassword;